import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { ChatMessage } from "../lib/types";

const STORAGE_KEY = "groky.chat.history.v1";

function extractPrompts(text: string): string[] {
  const out: string[] = [];
  const re = /```prompt\s*\n([\s\S]*?)```/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[1].trim());
  return out;
}

export default function ChatTab({
  onUseImagePrompt,
  onUseVideoPrompt,
  onError,
}: {
  onUseImagePrompt: (prompt: string) => void;
  onUseVideoPrompt: (prompt: string) => void;
  onError: (e: unknown) => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ChatMessage[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText]);

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;
    const userMsg: ChatMessage = { role: "user", content: text, ts: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setStreaming(true);
    setStreamingText("");

    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const full = await api.chatStream(
        next,
        (chunk) => setStreamingText((s) => s + chunk),
        ac.signal,
      );
      setMessages((m) => [...m, { role: "assistant", content: full, ts: Date.now() }]);
    } catch (e) {
      if ((e as Error).name !== "AbortError") onError(e);
    } finally {
      setStreaming(false);
      setStreamingText("");
      abortRef.current = null;
    }
  }

  function clearAll() {
    if (!confirm("Clear chat history?")) return;
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  }

  return (
    <div className="h-full flex flex-col">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !streaming && (
          <div className="text-zinc-500 text-sm max-w-prose">
            Ask Grok to brainstorm cinematic prompts. When it wraps a prompt in a{" "}
            <code className="text-zinc-300">```prompt</code> block, you'll see buttons to send it
            straight to the Image or Video tab.
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble key={i} message={m} onUseImagePrompt={onUseImagePrompt} onUseVideoPrompt={onUseVideoPrompt} />
        ))}
        {streaming && (
          <Bubble
            message={{ role: "assistant", content: streamingText + "▍", ts: Date.now() }}
            onUseImagePrompt={onUseImagePrompt}
            onUseVideoPrompt={onUseVideoPrompt}
            live
          />
        )}
      </div>
      <div className="border-t border-zinc-900 p-3 flex gap-2">
        <textarea
          className="input resize-none flex-1"
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Describe what you want to make…"
          disabled={streaming}
        />
        <div className="flex flex-col gap-2">
          <button className="btn-primary" onClick={send} disabled={streaming || !input.trim()}>
            {streaming ? "…" : "Send"}
          </button>
          <button className="btn-ghost text-xs" onClick={clearAll}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

function Bubble({
  message,
  onUseImagePrompt,
  onUseVideoPrompt,
  live,
}: {
  message: ChatMessage;
  onUseImagePrompt: (p: string) => void;
  onUseVideoPrompt: (p: string) => void;
  live?: boolean;
}) {
  const isUser = message.role === "user";
  const prompts = isUser ? [] : extractPrompts(message.content);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80ch] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed
        ${isUser ? "bg-violet-500/20 ring-1 ring-violet-500/30" : "bg-zinc-900 ring-1 ring-zinc-800"}`}
      >
        <div>{message.content}</div>
        {!live && prompts.length > 0 && (
          <div className="mt-2 space-y-2">
            {prompts.map((p, i) => (
              <div key={i} className="rounded bg-zinc-950/60 p-2 text-xs flex flex-col gap-2">
                <code className="text-zinc-300 whitespace-pre-wrap">{p}</code>
                <div className="flex gap-2">
                  <button className="btn-secondary text-xs" onClick={() => onUseImagePrompt(p)}>
                    Use as image prompt
                  </button>
                  <button className="btn-secondary text-xs" onClick={() => onUseVideoPrompt(p)}>
                    Use as video prompt
                  </button>
                  <button className="btn-ghost text-xs" onClick={() => navigator.clipboard.writeText(p)}>
                    Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
